import React, {useMemo, useState} from 'react';
import {Button, DatePicker, Form, Input, InputNumber, Select, Table} from "antd";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import {internalApi} from "../../../common/axios";
import {useQueryClient} from "@tanstack/react-query";

const TIME_QUICK_BUTTONS = [
    {label: '1h', value: 1},
    {label: '2h', value: 2},
    {label: '3h', value: 3},
    {label: '4h', value: 4},
    {label: '1D', value: 24},
];

const packageColumns = [
    {
        title: '평균 결제 금액(달러)', key: 'avrPayment', width: 200,
        render: (_, r) => r.minAvrPayment === 0 && r.maxAvrPayment === 0
            ? '제한 없음'
            : `$${r.minAvrPayment} ~ $${r.maxAvrPayment}`,
    },
    {
        title: '레벨', key: 'level', width: 140,
        render: (_, r) => r.minLevel === 0 && r.maxLevel === 0
            ? '제한 없음'
            : `${r.minLevel} ~ ${r.maxLevel}`,
    },
    {title: '할인', dataIndex: 'saleType', key: 'saleType', width: 80},
    {title: '패키지 인덱스', dataIndex: 'packageIndex', key: 'packageIndex', width: 120},
    {title: '패키지 이름', dataIndex: 'packageName', key: 'packageName'},
];

const PersonalPackageRegisterModal = ({personalPackage, groupItems, handleClose}) => {
    const queryClient = useQueryClient();
    const [saving, setSaving] = useState(false);

    const [begin, setBegin] = useState(dayjs().hour(9).minute(0).second(0));
    const [onOff, setOnOff] = useState(0);
    const [packageTime, setPackageTime] = useState(1);
    const [saleCount, setSaleCount] = useState(1);
    const [gap, setGap] = useState(1);

    // 종료 시간: 시작일 + 판매일 수, 항상 08:59:59
    const end = useMemo(() =>
        begin.add(saleCount, 'day').hour(8).minute(59).second(59),
        [begin, saleCount]
    );

    const buildPayload = () => ({
        groupIdx: personalPackage.seq,
        begin: begin.unix(),
        onOff,
        packageTime,
        saleCount,
        gap,
    });

    // 저장: 하루 1슬롯씩 판매일 수만큼
    const handleSave = async () => {
        setSaving(true);
        try {
            await internalApi.post('store_manager/personalPackage/register', {data: buildPayload()});
            toast.success('패키지 등록 완료');
            queryClient.invalidateQueries({queryKey: ['personalTableData']});
            handleClose();
        } catch {
            toast.error('패키지 등록 실패');
        } finally {
            setSaving(false);
        }
    };

    // 반복 상품 등록: gap 간격으로 기간 내 모든 슬롯
    const handleRepeatRegister = async () => {
        setSaving(true);
        try {
            await internalApi.post('store_manager/personalPackage/repeat', {data: buildPayload()});
            toast.success('반복 이벤트 등록 완료');
            queryClient.invalidateQueries({queryKey: ['personalTableData']});
            handleClose();
        } catch {
            toast.error('반복 이벤트 등록 실패');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <Form layout="inline" style={{marginBottom: 8}}>
                {/* 1. 개인화 메모 */}
                <Form.Item label="개인화 메모" style={{width: '65%', marginBottom: 8}}>
                    <Input value={personalPackage.memo || '-'} size="small" disabled style={{color: '#333'}}/>
                </Form.Item>

                {/* 2. OnOff */}
                <Form.Item label="OnOff" style={{marginBottom: 8, width: '30%'}}>
                    <Select value={onOff} onChange={setOnOff} size="small" style={{width: '100%'}}>
                        <Select.Option value={1}>On</Select.Option>
                        <Select.Option value={0}>Off</Select.Option>
                    </Select>
                </Form.Item>

                {/* 3. 시작 시간 */}
                <Form.Item label="시작 시간" style={{marginBottom: 8, width: '48%'}}>
                    <DatePicker
                        value={begin}
                        onChange={date => date && setBegin(date)}
                        showTime={{format: 'HH:mm'}}
                        format="YYYY-MM-DD HH:mm"
                        size="small"
                        style={{width: '100%', marginBottom: 4}}
                    />
                    <div style={{display: 'flex', gap: 4, marginTop: 4}}>
                        <Button size="small" onClick={() => setBegin(dayjs().hour(begin.hour()).minute(begin.minute()).second(0))}>Today</Button>
                        <Button size="small" onClick={() => setBegin(prev => prev.add(1, 'day'))}>시작일 +1</Button>
                        <Button size="small" onClick={() => setBegin(prev => prev.subtract(1, 'day'))}>시작일 -1</Button>
                    </div>
                </Form.Item>

                {/* 7. 종료 시간 (자동 계산) */}
                <Form.Item label="종료 시간" style={{marginBottom: 8, width: '48%'}}>
                    <DatePicker
                        value={end}
                        showTime={{format: 'HH:mm'}}
                        format="YYYY-MM-DD HH:mm"
                        size="small"
                        disabled
                        style={{width: '100%', background: '#f5f5f5'}}
                    />
                </Form.Item>

                {/* 4. 판매 기간 (시간) */}
                <Form.Item label="판매 기간 (시간)" style={{marginBottom: 8, width: '48%'}}>
                    <div style={{display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4}}>
                        <Button size="small" onClick={() => setPackageTime(v => Math.max(1, v - 24))}>-1일</Button>
                        <Button size="small" onClick={() => setPackageTime(v => Math.max(1, v - 1))}>-1h</Button>
                        <InputNumber
                            min={1}
                            value={packageTime}
                            onChange={v => setPackageTime(v || 1)}
                            size="small"
                            addonAfter="h"
                            style={{width: 90}}
                        />
                        <Button size="small" onClick={() => setPackageTime(v => v + 1)}>+1h</Button>
                        <Button size="small" onClick={() => setPackageTime(v => v + 24)}>+1일</Button>
                    </div>
                    <div style={{display: 'flex', gap: 4}}>
                        {TIME_QUICK_BUTTONS.map(btn => (
                            <Button
                                key={btn.value}
                                size="small"
                                type={packageTime === btn.value ? 'primary' : 'default'}
                                onClick={() => setPackageTime(btn.value)}
                            >
                                {btn.label}
                            </Button>
                        ))}
                    </div>
                </Form.Item>

                {/* 6. 시작 시간과 다음 시작 시간의 간격 */}
                <Form.Item label="시작 시간과 다음 시작 시간의 간격" style={{marginBottom: 8, width: '48%'}}>
                    <InputNumber
                        min={1}
                        max={720}
                        value={gap}
                        onChange={v => setGap(v || 1)}
                        size="small"
                        addonAfter="시간"
                        style={{width: '100%'}}
                    />
                </Form.Item>

                {/* 5. 판매일 수 */}
                <Form.Item label="판매일 수 (반복 횟수)" style={{marginBottom: 8, width: '48%'}}>
                    <InputNumber
                        min={1}
                        value={saleCount}
                        onChange={v => setSaleCount(v || 1)}
                        size="small"
                        addonAfter="일"
                        style={{width: '100%'}}
                    />
                </Form.Item>
            </Form>

            <div style={{border: '2px solid #1e4d7b', borderRadius: 8, padding: 12, marginTop: 8}}>
                <div style={{fontWeight: 'bold', marginBottom: 8}}>패키지 목록</div>
                <Table
                    columns={packageColumns}
                    dataSource={groupItems.map((g, i) => ({...g, key: g.seq || i}))}
                    pagination={false}
                    size="small"
                    scroll={{x: 'max-content'}}
                />
            </div>

            <div style={{marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
                <Button onClick={handleClose}>취소</Button>
                <Button type="dashed" loading={saving} onClick={handleRepeatRegister}>반복 상품 등록</Button>
                <Button type="primary" loading={saving} onClick={handleSave}>저장</Button>
            </div>
        </div>
    );
};

export default PersonalPackageRegisterModal;
